import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import BrandModal from "../../components/modal/brandModal";
import Datatable from "../../components/common/datatable";
import SortableDatatable from "../../components/common/SortableDatatable";
import { useViewBrandQuery } from "../../hooks/brand/brand.all";
import { useDeleteBrand } from "../../hooks/brand/brand.delete";
import { useBrandStore } from "../../hooks/brand/brand.store";
import { useBrandUpdate } from "../../hooks/brand/brand.update";
import { useBrandReorder } from "../../hooks/brand/brand.reorder";
import { toast } from "react-toastify";
import SearchItem from "../../components/searchItem";
import ActionMenu from "../../components/common/menu";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";
import FallbackImage from "../../components/common/fallbackImage";

const Brand = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editBrand, setEditBrand] = useState(null);
  const [modalKey, setModalKey] = useState(0);
  const { mutate: addBrand, isPending: isAdding } = useBrandStore();
  const [mode, setMode] = useState("create");
  const [localBrand, setLocalBrand] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const { mutate: updateBrand } = useBrandUpdate();
  const { mutate: reorderBrands, isPending: isReordering } = useBrandReorder();
  const {
    data: brandResponse,
    isLoading,
    isFetching,
    isError,
    refetch: refetchBrands,
  } = useViewBrandQuery({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });
  const { mutate: removeBrand, isPending: isDeleting } = useDeleteBrand();

  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!brandResponse) return;

    const sorted = [...(brandResponse.brands ?? [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    setLocalBrand(sorted);

    const apiPagination = brandResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [brandResponse, pageSize]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const canReorder =
    !debouncedSearchTerm.trim() && pagination.totalPages <= 1;

  const handleReorder = (reordered) => {
    const previous = localBrand;
    setLocalBrand(reordered);

    reorderBrands(reordered, {
      onSuccess: () => {
        toast.success("Brand order updated");
        refetchBrands();
      },
      onError: () => {
        setLocalBrand(previous);
        refetchBrands();
      },
    });
  };

  const handleAddBrand = () => {
    setEditBrand(null);
    setMode("create");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleEdit = (brand) => {
    setEditBrand(brand);
    setMode("edit");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleView = (brand) => {
    setEditBrand(brand);
    setMode("view");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleDelete = (brand) => {
    setEditBrand(brand);
    setMode("delete");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleSave = (payload) => {
    const mutationOptions = {
      onSuccess: () => {
        setIsModalOpen(false);
        setEditBrand(null);
        refetchBrands();
      },
      onError: (err) => {
        alert(
          `Operation failed: ${err.response?.data?.message || err.message || "Please try again."}`,
        );
      },
    };

    if (mode === "delete") {
      removeBrand(editBrand._id || editBrand.id, mutationOptions);
      return;
    }

    const body = new FormData();
    body.append("title", payload.title || "");
    body.append("slug", payload.slug || "");
    body.append("description", payload.description || "");
    body.append("order", payload.order ?? "");
    body.append("isActive", payload.isActive ?? true);

    if (payload.image instanceof File) {
      body.append("image", payload.image);
    }

    if (mode === "edit" && editBrand) {
      updateBrand(
        {
          id: editBrand._id || editBrand.id,
          body: body,
        },
        mutationOptions,
      );
    } else {
      addBrand(body, mutationOptions);
    }
  };

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (value) => {
        if (!value) return "-";
        return (
          <FallbackImage
            src={value}
            alt="Logo"
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
    { key: "title", label: "Title" },
    { key: "description", label: "Description" },
    {
      key: "isActive",
      label: "Status",
      render: (_, row) => {
        const active =
          row.isActive === true ||
          row.isActive === "Active" ||
          row.isActive === 1 ||
          row.status === true ||
          row.status === "Active" ||
          row.status === 1;
        return (
          <span
            className={`status-badge ${active ? "status-active" : "status-expired"}`}
          >
            {active ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <ActionMenu
          row={row}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading brands</div>;

  return (
    <>
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
            Manage your brands
          </p>
        </div>
        <Button onClick={handleAddBrand}>+ Add Brand</Button>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localBrand}
            searchField={["title", "description"]}
            placeholder="Search brands..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
          />
          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      <BrandModal
        key={modalKey}
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => setIsModalOpen(false)}
        editData={editBrand}
        mode={mode}
        viewMode={mode === "view"}
        onSubmit={handleSave}
        isLoading={isAdding}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setEditBrand(null);
        }}
        onConfirm={() => handleSave()}
        title="Delete Brand"
        itemName={editBrand?.title}
        isLoading={isDeleting}
      />

      {canReorder ? (
        <>
          <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "8px" }}>
            {isReordering ? " Saving…" : "Drag rows to reorder brands."}
          </p>
          <SortableDatatable
            data={localBrand}
            columns={columns}
            onReorder={handleReorder}
            disabled={isReordering}
          />
        </>
      ) : (
        <Datatable data={localBrand} columns={columns} />
      )}

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default Brand;
